import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Lightbulb, Calendar, CheckCircle, Clock, XCircle, Star } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface Suggestion {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'approved' | 'rejected' | 'implemented' | 'em_desenvolvimento';
  admin_notes?: string;
  created_at: string;
  user_id: string;
}

interface Update {
  id: string;
  version: string;
  title: string;
  description: string;
  release_date: string;
  is_major: boolean;
  created_at: string;
}

export default function Suggestions() {
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [updates, setUpdates] = useState<Update[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("suggestions");

  useEffect(() => {
    loadSuggestions();
    loadUpdates();
  }, [user]);

  const loadSuggestions = async () => {
    try {
      let query = supabase
        .from('suggestions')
        .select('*');

      // Filtrar sugestões baseado na visibilidade por status
      if (user) {
        // Admin vê todas, usuário vê: públicas + suas próprias pendentes
        const { data: userData } = await supabase
          .from('users')
          .select('email')
          .eq('id', user.id)
          .single();
        
        const isAdmin = userData?.email === 'wagner.jeveaux@gmail.com';
        
        if (!isAdmin) {
          query = query.or(`status.in.(em_desenvolvimento,implemented),and(status.eq.pending,user_id.eq.${user.id})`);
        }
      } else {
        // Usuário não logado só vê públicas
        query = query.in('status', ['em_desenvolvimento', 'implemented']);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      setSuggestions((data || []) as Suggestion[]);
    } catch (error) {
      console.error('Erro ao carregar sugestões:', error);
      toast.error('Erro ao carregar sugestões');
    }
  };

  const loadUpdates = async () => {
    try {
      const { data, error } = await supabase
        .from('updates')
        .select('*')
        .order('release_date', { ascending: false });

      if (error) throw error;
      setUpdates(data || []);
    } catch (error) {
      console.error('Erro ao carregar atualizações:', error);
      toast.error('Erro ao carregar atualizações');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast.error('Você precisa estar logado para enviar uma sugestão');
      return;
    }

    if (!title.trim() || !description.trim()) {
      toast.error('Por favor, preencha todos os campos');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('suggestions')
        .insert({
          title: title.trim(),
          description: description.trim(),
          user_id: user.id,
        });

      if (error) throw error;

      toast.success('Sugestão enviada com sucesso!');
      setTitle("");
      setDescription("");
      loadSuggestions();
    } catch (error) {
      console.error('Erro ao enviar sugestão:', error);
      toast.error('Erro ao enviar sugestão');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      approved: { label: 'Aprovada', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
      implemented: { label: 'Implementada', color: 'bg-green-100 text-green-800', icon: Star },
      rejected: { label: 'Rejeitada', color: 'bg-red-100 text-red-800', icon: XCircle },
      em_desenvolvimento: { label: 'Em Desenvolvimento', color: 'bg-purple-100 text-purple-800', icon: CheckCircle },
    };

    const config = statusConfig[status as keyof typeof statusConfig];
    const Icon = config.icon;

    return (
      <Badge variant="secondary" className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const getVersionBadge = (version: string, isMajor: boolean) => {
    return (
      <Badge variant={isMajor ? "default" : "secondary"} className="font-mono">
        v{version}
      </Badge>
    );
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <Lightbulb className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold">Sugestões e Atualizações</h1>
          <p className="text-muted-foreground">
            Compartilhe suas ideias e acompanhe as melhorias da plataforma
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="suggestions">Enviar Sugestão</TabsTrigger>
          <TabsTrigger value="updates">Atualizações</TabsTrigger>
        </TabsList>

        <TabsContent value="suggestions" className="space-y-6">
          {/* Formulário de Sugestão */}
          <Card>
            <CardHeader>
              <CardTitle>Nova Sugestão</CardTitle>
              <CardDescription>
                Compartilhe sua ideia para melhorar a plataforma Justiçai
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium mb-2">
                    Título da Sugestão
                  </label>
                  <Input
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Descreva brevemente sua sugestão..."
                    disabled={loading}
                    maxLength={100}
                  />
                </div>
                <div>
                  <label htmlFor="description" className="block text-sm font-medium mb-2">
                    Descrição Detalhada
                  </label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Explique sua sugestão em detalhes, como ela melhoraria a plataforma e qualquer contexto adicional..."
                    rows={4}
                    disabled={loading}
                    maxLength={500}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {description.length}/500 caracteres
                  </p>
                </div>
                <Button type="submit" disabled={loading || !user}>
                  {loading ? 'Enviando...' : 'Enviar Sugestão'}
                </Button>
                {!user && (
                  <p className="text-sm text-muted-foreground">
                    Você precisa estar logado para enviar sugestões.
                  </p>
                )}
              </form>
            </CardContent>
          </Card>

          {/* Lista de Sugestões */}
          <Card>
            <CardHeader>
              <CardTitle>Sugestões da Comunidade</CardTitle>
              <CardDescription>
                Veja as sugestões enviadas por outros usuários
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {suggestions.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    Nenhuma sugestão encontrada. Seja o primeiro a contribuir!
                  </p>
                ) : (
                  suggestions.map((suggestion) => (
                    <div key={suggestion.id} className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-start justify-between">
                        <h3 className="font-semibold text-lg">{suggestion.title}</h3>
                        {getStatusBadge(suggestion.status)}
                      </div>
                      <p className="text-muted-foreground">{suggestion.description}</p>
                      {suggestion.admin_notes && (
                        <div className="bg-muted p-3 rounded-md">
                          <p className="text-sm">
                            <strong>Nota da equipe:</strong> {suggestion.admin_notes}
                          </p>
                        </div>
                      )}
                       <div className="flex items-center justify-between text-xs text-muted-foreground">
                         <span>
                           Enviado por usuário
                         </span>
                         <div className="flex items-center gap-2">
                           <span>
                             {format(new Date(suggestion.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                           </span>
                           {user && suggestion.user_id === user.id && (
                             <Button
                               variant="ghost"
                               size="sm"
                               onClick={async () => {
                                 try {
                                   const { error } = await supabase
                                     .from('suggestions')
                                     .delete()
                                     .eq('id', suggestion.id);
                                   
                                   if (error) throw error;
                                   toast.success('Sugestão excluída com sucesso!');
                                   loadSuggestions();
                                 } catch (error) {
                                   console.error('Erro ao excluir sugestão:', error);
                                   toast.error('Erro ao excluir sugestão');
                                 }
                               }}
                               className="text-red-600 hover:text-red-700 h-6"
                             >
                               Excluir
                             </Button>
                           )}
                         </div>
                       </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="updates" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Histórico de Atualizações
              </CardTitle>
              <CardDescription>
                Confira todas as melhorias e novidades implementadas na plataforma
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {updates.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    Nenhuma atualização encontrada.
                  </p>
                ) : (
                  updates.map((update, index) => (
                    <div key={update.id} className="relative">
                      {index !== updates.length - 1 && (
                        <div className="absolute left-4 top-12 bottom-0 w-px bg-border" />
                      )}
                      <div className="flex items-start gap-4">
                        <div className="bg-primary text-primary-foreground rounded-full p-2 shrink-0">
                          <Star className="h-4 w-4" />
                        </div>
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-3">
                            {getVersionBadge(update.version, update.is_major)}
                            <h3 className="font-semibold text-lg">{update.title}</h3>
                          </div>
                          <p className="text-muted-foreground">{update.description}</p>
                          <p className="text-sm text-muted-foreground">
                            Lançado em {format(new Date(update.release_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}